// features/customer/components/CustomerUserManagement.tsx
import React, { useState, useEffect } from "react";
import {
  Pencil,
  Eye,
  ClipboardList,
  Trash2,
  Power,
  RotateCcw,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../app/store";
import { API_V1_BASE_URL } from "../../config/api";
import { superAdminApi } from "../superAdmin/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatAuditAction } from "../../config/auditActions";
import { hasPermission, useHasPermission } from "../../hooks/usePermission";

interface InternalUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: any;
  status: "active" | "deactivated";
  roleId: any;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  createdBy: string;
  department?: string;
  phoneNumber?: string;
  max_users?: number;
  avatar?: string;
  permissions?: string[];
  pages?: string[];
}

interface AuditLog {
  id: string;
  action: string;
  userId: string;
  userEmail: string;
  performedBy: string;
  performedByRole: string;
  timestamp: string;
  details: string;
  ipAddress: string;
  location?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
}

interface AuditTenant {
  id: string;
  name: string;
}

interface PagePermission {
  id: string;
  pageName: string;
  path: string;
  icon: string;
  description: string;
  requiredPermission: string; // matches backend permission codes, e.g. 'USER_VIEW'
}

interface RolePermission {
  // role: 'customer_admin' | 'customer_manager' | 'customer_viewer';
  role: string;
  name: string;
  description: string;
  permissions: string[];
  pages: string[];
}

interface CustomerUserManagementProps {
  tenantId: string;
}
interface TenantPlan {
  maxUsers: number;
  storageValue: number;
  storageUnit: "MB" | "GB" | "TB";
}

const DEMO_PRICING = { perUser: 10, perGb: 0.5 }; // USD, placeholder rates
const toGb = (v: number, u: string) =>
  u === "TB" ? v * 1024 : u === "MB" ? v / 1024 : v;
const formatUSD = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

// const MAX_ALLOWED_USERS = 30;

const IconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  colorClass: string;
}> = ({ icon, label, onClick, colorClass }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      aria-label={label}
      className={`p-2 rounded-lg transition ${colorClass}`}
    >
      {icon}
    </button>

    <span
      className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2
            whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white
            opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
            transition-all duration-150 z-10"
    >
      {label}
    </span>
  </div>
);

const CustomerUserManagement: React.FC<CustomerUserManagementProps> = ({
  tenantId,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canCreateUser = useHasPermission("USER_CREATE");
  const canUpdateUser = useHasPermission("USER_UPDATE");
  const canDeleteUser = useHasPermission("USER_DELETE");
  const canAuditUser = useHasPermission("USER_AUDIT");
  const canUpdateRole = useHasPermission("ROLE_UPDATE");

  const [maxAllowedUsers, setMaxAllowedUsers] = useState(0);
  console.log("Current User", currentUser);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<InternalUser[]>([]);
  const [statusFilter, setStatusFilter] = useState<
    "active" | "deactivated" | "all"
  >("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showUserLimitWarning, setShowUserLimitWarning] = useState(false);
  const [selectedUser, setSelectedUser] = useState<InternalUser | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditTenants, setAuditTenants] = useState<AuditTenant[]>([]);
  const [selectedAuditTenantId, setSelectedAuditTenantId] = useState("");
  const [selectedAuditAction, setSelectedAuditAction] = useState("");
  const [availableAuditActions, setAvailableAuditActions] = useState<string[]>(
    [],
  );
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState("");
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showUserAuditModal, setShowUserAuditModal] = useState(false);
  const [selectedUserForAudit, setSelectedUserForAudit] =
    useState<InternalUser | null>(null);
  const [userAuditLogs, setUserAuditLogs] = useState<AuditLog[]>([]);
  const [selectedRolePermissions, setSelectedRolePermissions] =
    useState<RolePermission | null>(null);
  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [editablePermissions, setEditablePermissions] = useState<string[]>([]);
  const [editablePages, setEditablePages] = useState<string[]>([]);
  const [newPermission, setNewPermission] = useState("");
  const [newPageId, setNewPageId] = useState("");
  const [uploadHistory, setUploadHistory] = useState<any[]>([]);
  const [createRoleAccess, setCreateRoleAccess] = useState<{
    permissions: string[];
    pages: PagePermission[];
  } | null>(null);
  const [createRoleAccessLoading, setCreateRoleAccessLoading] = useState(false);
  const [createRoleAccessError, setCreateRoleAccessError] = useState("");
  const [createSelectedPermissions, setCreateSelectedPermissions] = useState<
    string[]
  >([]);
  const [createSelectedPages, setCreateSelectedPages] = useState<string[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [rolesListLoading, setRolesListLoading] = useState(false);
  const [rolesListError, setRolesListError] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [tenantPlan, setTenantPlan] = useState<TenantPlan | null>(null);
  const [planPaid, setPlanPaid] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "success"
  >("idle");
  const [ticketStatus, setTicketStatus] = useState<
    "idle" | "submitting" | "raised"
  >("idle");
  const [ticketId, setTicketId] = useState("");
  const [ticketForm, setTicketForm] = useState({
    requestedUsers: "",
    requestedStorageValue: "",
    requestedStorageUnit: "GB" as "MB" | "GB" | "TB",
  });
  const [ticketError, setTicketError] = useState("");
  const [ticketSummary, setTicketSummary] = useState<{
    users: number;
    storage: string;
  } | null>(null);
  // Form state for create/edit
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "" as string,
    roleId: "",
    department: "",
    phoneNumber: "",
  });

  const [formErrors, setFormErrors] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  // Page permissions mapping
  const pagePermissions: PagePermission[] = [
    {
      id: "dashboard",
      pageName: "Dashboard",
      path: "/dashboard",
      icon: "📊",
      description: "View main dashboard and analytics",
      requiredPermission: "DASHBOARD_VIEW",
    },
    {
      id: "campaigns",
      pageName: "Campaigns",
      path: "/campaigns",
      icon: "📢",
      description: "View and manage marketing campaigns",
      requiredPermission: "CAMPAIGN_VIEW",
    },
    {
      id: "create-campaign",
      pageName: "Create Campaign",
      path: "/campaigns/create",
      icon: "➕",
      description: "Create new marketing campaigns",
      requiredPermission: "CAMPAIGN_CREATE",
    },
    {
      id: "edit-campaign",
      pageName: "Edit Campaign",
      path: "/campaigns/edit/*",
      icon: "✏️",
      description: "Edit existing campaigns",
      requiredPermission: "CAMPAIGN_UPDATE",
    },
    {
      id: "delete-campaign",
      pageName: "Delete Campaign",
      path: "/campaigns/delete",
      icon: "🗑️",
      description: "Delete campaigns",
      requiredPermission: "CAMPAIGN_DELETE",
    },
    {
      id: "user-management",
      pageName: "User Management",
      path: "/users",
      icon: "👥",
      description: "Manage internal users",
      requiredPermission: "USER_VIEW",
    },
    {
      id: "reports",
      pageName: "Reports & Analytics",
      path: "/reports",
      icon: "📈",
      description: "View reports and analytics",
      requiredPermission: "REPORT_VIEW",
    },
    {
      id: "financials",
      pageName: "Financial & Billing",
      path: "/financials",
      icon: "💰",
      description: "View financial and billing information",
      requiredPermission: "BILLING_VIEW",
    },
    {
      id: "settings",
      pageName: "Settings",
      path: "/settings",
      icon: "⚙️",
      description: "Access system settings",
      requiredPermission: "SETTINGS_VIEW",
    },
    {
      id: "guest-access",
      pageName: "Guest Access",
      path: "/guest-access",
      icon: "🔗",
      description: "Manage guest access links",
      requiredPermission: "GUEST_VIEW",
    },
    {
      id: "audit-logs",
      pageName: "Audit Logs",
      path: "/audit-logs",
      icon: "📋",
      description: "View system audit logs",
      requiredPermission: "AUDIT_LOG_VIEW",
    },
    {
      id: "api-access",
      pageName: "API Access",
      path: "/api-keys",
      icon: "🔑",
      description: "Manage API keys and integrations",
      requiredPermission: "SETTINGS_CONFIGURE",
    },
  ];

  // Available permissions list for adding
  const availablePermissions = [
    "create",
    "read",
    "update",
    "delete",
    "manage_users",
    "manage_settings",
    "view_all",
    "view_reports",
    "manage_campaigns",
  ];

  // Role definitions with permissions
  // const [roleDefinitions, setRoleDefinitions] = useState<RolePermission[]>([
  //     {
  //         role: 'customer_admin',
  //         name: 'Administrator',
  //         description: 'Full system access - can manage users, settings, and all content',
  //         permissions: ['create', 'read', 'update', 'delete', 'manage_users', 'manage_settings', 'view_all'],
  //         pages: pagePermissions.filter(p => p.allowedRoles.includes('customer_admin')).map(p => p.id)
  //     },
  //     {
  //         role: 'customer_manager',
  //         name: 'Manager',
  //         description: 'Can manage campaigns and view reports, but cannot manage users or settings',
  //         permissions: ['create', 'read', 'update', 'view_reports', 'manage_campaigns'],
  //         pages: pagePermissions.filter(p => p.allowedRoles.includes('customer_manager')).map(p => p.id)
  //     },
  //     {
  //         role: 'customer_viewer',
  //         name: 'Viewer',
  //         description: 'Read-only access - can only view dashboards and reports',
  //         permissions: ['read', 'view_reports'],
  //         pages: pagePermissions.filter(p => p.allowedRoles.includes('customer_viewer')).map(p => p.id)
  //     }
  // ]);

  const [roleDefinitions, setRoleDefinitions] = useState<RolePermission[]>([]);

  useEffect(() => {
    loadUsers();
    loadAuditTenants();
    loadAuditActions();
    loadAuditLogs();
    loadUploadHistory();
    loadRoles();
    loadTenantPlan();
  }, [tenantId]);

  const planKey = tenantPlan
    ? `planPaid:${tenantId}:${tenantPlan.maxUsers}:${tenantPlan.storageValue}${tenantPlan.storageUnit}`
    : "";

  useEffect(() => {
    if (planKey) setPlanPaid(localStorage.getItem(planKey) === "true");
  }, [planKey]);

  useEffect(() => {
    if (showUserLimitWarning && tenantPlan) {
      setTicketForm({
        requestedUsers: String(tenantPlan.maxUsers + 1),
        requestedStorageValue: tenantPlan.storageValue
          ? String(tenantPlan.storageValue)
          : "",
        requestedStorageUnit: tenantPlan.storageUnit,
      });
      setTicketError("");
    }
  }, [showUserLimitWarning]);

  useEffect(() => {
    filterUsers();
  }, [users, statusFilter, roleFilter, searchTerm]);

  useEffect(() => {
    if (!showCreateModal) return;
    applyRoleAccess(formData.roleId);
  }, [formData.roleId, showCreateModal]);

  const trackUserSession = () => {
    // Simulate tracking user login/logout
    const mockLocation = getCurrentLocation();

    // Add login audit log for current user
    addAuditLog(
      "LOGIN",
      currentUser?.id || "system",
      currentUser?.email || "system",
      "User logged into the system",
      mockLocation,
    );
  };

  const getCurrentLocation = () => {
    // In production, this would use IP geolocation API
    return {
      city: "San Francisco",
      country: "USA",
      timezone: "PST",
      coordinates: "37.7749° N, 122.4194° W",
    };
  };

  const loadTenantPlan = async () => {
    try {
      const t = await superAdminApi.getMyTenant();
      const plan: TenantPlan = {
        maxUsers: Number(t.max_users) || 0,
        storageValue: Number(t.storage_limit_value) || 0,
        storageUnit: (t.storage_limit_unit ||
          "GB") as TenantPlan["storageUnit"],
      };
      setTenantPlan(plan);
      setMaxAllowedUsers(plan.maxUsers);
    } catch (e) {
      console.error("Failed to load tenant plan", e);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setCreateRoleAccess(null);
    setCreateRoleAccessError("");
    setCreateSelectedPermissions([]);
    setCreateSelectedPages([]);
    setShowCreateModal(true);
  };

  const handleCreateUserClick = () => {
    if (!planPaid) {
      setPaymentStatus("idle");
      setShowPaymentModal(true);
      return;
    }
    if (!checkUserLimit()) return; // opens the "raise a ticket" modal
    openCreateModal();
  };

  const handlePayNow = () => {
    setPaymentStatus("processing");
    setTimeout(() => {
      if (planKey) localStorage.setItem(planKey, "true");
      setPlanPaid(true);
      setPaymentStatus("success");
      setTimeout(() => {
        setShowPaymentModal(false);
        setPaymentStatus("idle");
        toast.success("Payment successful. You can now create users.");
        if (checkUserLimit()) openCreateModal();
      }, 1400);
    }, 2500);
  };

  const handleRaiseTicket = () => {
    const users = Number(ticketForm.requestedUsers);
    const storage = Number(ticketForm.requestedStorageValue);

    if (!Number.isInteger(users) || users <= maxAllowedUsers) {
      setTicketError(
        `Requested users must be a whole number greater than your current limit (${maxAllowedUsers}).`,
      );
      return;
    }
    if (!storage || storage <= 0) {
      setTicketError("Please enter the storage you need.");
      return;
    }

    setTicketError("");
    setTicketSummary({
      users,
      storage: `${storage} ${ticketForm.requestedStorageUnit}`,
    });
    setTicketStatus("submitting");
    setTimeout(() => {
      setTicketId(`TKT-${Date.now().toString().slice(-6)}`);
      setTicketStatus("raised");
    }, 1800);
  };

  const closeLimitModal = () => {
    setShowUserLimitWarning(false);
    setTicketStatus("idle");
    setTicketId("");
    setTicketError("");
    setTicketSummary(null);
  };

  const loadUsers = async () => {
    try {
      let response: any;
      if (currentUser?.role !== "super_admin") {
        response = await superAdminApi.getMyUsers({ limit: 1000 });
      } else {
        response = await superAdminApi.getUsers();
      }

      const apiUsers =
        response.users || response.data?.users || response.data || [];

      const mappedUsers = apiUsers.map((user: any) => ({
        id: user.id,

        email: user.email,

        firstName: user.first_name,

        lastName: user.last_name,

        role: user.role.toLowerCase().replace(/-/g, "_").replace(/ /g, "_"),

        status: String(user.status || "active").toLowerCase(),

        createdAt: user.created_at,

        updatedAt: user.updated_at,

        lastLogin: user.last_login
          ? new Date(user.last_login).toLocaleString()
          : "-",

        createdBy: user.created_by
          ? `${user.created_by.firstName ?? ""} ${user.created_by.lastName ?? ""}`.trim()
          : "Super Admin",

        department: user.department,

        phoneNumber: user.phone_number,
        max_users: user.max_users,
      }));

      setUsers(mappedUsers);

      if (apiUsers.length > 0) {
        setMaxAllowedUsers(apiUsers[0].max_users ?? 0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadAuditTenants = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_V1_BASE_URL}/audit-logs/tenants`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json().catch(() => null);
      if (response.ok && result?.success) {
        setAuditTenants(result.data || []);
      }
    } catch {
      setAuditTenants([]);
    }
  };

  const loadAuditLogs = async (
    auditTenantId = selectedAuditTenantId,
    action = selectedAuditAction,
  ) => {
    setAuditLogsLoading(true);
    setAuditLogsError("");

    try {
      const token = localStorage.getItem("accessToken");
      const params = new URLSearchParams({ limit: "100" });
      if (auditTenantId) params.set("tenantId", auditTenantId);
      if (action) params.set("action", action);
      const response = await fetch(
        `${API_V1_BASE_URL}/audit-logs?${params.toString()}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to load audit logs");
      }

      const logs = result.data?.data || [];
      setAuditLogs(
        logs.map((log: any) => ({
          id: log.id,
          action: log.action,
          userId: log.user_id || log.userId,
          userEmail: log.user_email || log.userEmail,
          performedBy: log.performed_by_email || log.performedByEmail,
          performedByRole: log.performed_by_role || log.performedByRole,
          timestamp: log.timestamp,
          details: log.details,
          ipAddress: log.ip_address || log.ipAddress,
          location: [log.location_city, log.location_country]
            .filter(Boolean)
            .join(", "),
          userAgent: log.user_agent || log.userAgent,
          metadata: log.metadata,
        })),
      );
    } catch (error) {
      setAuditLogs([]);
      setAuditLogsError(
        error instanceof Error ? error.message : "Failed to load audit logs",
      );
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const loadAuditActions = async (auditTenantId = selectedAuditTenantId) => {
    try {
      const token = localStorage.getItem("accessToken");
      const params = new URLSearchParams();
      if (auditTenantId) params.set("tenantId", auditTenantId);
      const response = await fetch(
        `${API_V1_BASE_URL}/audit-logs/actions?${params.toString()}`,
        {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        },
      );
      const result = await response.json().catch(() => null);
      setAvailableAuditActions(
        response.ok && result?.success ? result.data || [] : [],
      );
    } catch {
      setAvailableAuditActions([]);
    }
  };

  const loadRoles = async () => {
    setRolesListLoading(true);
    setRolesListError("");
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_V1_BASE_URL}/roles`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json().catch(() => null);

      // API returns a plain array in this deployment; fall back to
      // result.data / result.data.data in case of a wrapped response
      const list: any[] = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : Array.isArray(result?.data?.data)
            ? result.data.data
            : [];

      setRolesList(list);

      const mappedRoles: RolePermission[] = list.map((role: any) => ({
        role: role.role?.toLowerCase() || role.role_name?.toLowerCase(),
        name: role.description || role.role_name,
        description: role.description || "",
        permissions: Array.isArray(role.permissions)
          ? role.permissions.map((p: any) => p.permissionCode)
          : [],
        pages: [], // Backend doesn't return pages yet
      }));

      setRoleDefinitions(mappedRoles);
    } catch (error) {
      setRolesList([]);
      setRolesListError(
        error instanceof Error ? error.message : "Failed to load roles",
      );
    } finally {
      setRolesListLoading(false);
    }
  };

  const loadUploadHistory = () => {
    setUploadHistory([]);
  };

  const filterUsers = () => {
    let filtered = [...users];
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (user) => user.status?.toLowerCase() === statusFilter.toLowerCase(),
      );
    }
    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName.toLowerCase().includes(term) ||
          user.lastName.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term) ||
          user.department?.toLowerCase().includes(term),
      );
    }
    setFilteredUsers(filtered);
  };

  const toggleCreatePermission = (perm: string) => {
    setCreateSelectedPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  };

  const toggleCreatePage = (pageId: string) => {
    setCreateSelectedPages((prev) =>
      prev.includes(pageId)
        ? prev.filter((p) => p !== pageId)
        : [...prev, pageId],
    );
  };

  const fetchRoleAccess = async (role: InternalUser["role"]) => {
    setCreateRoleAccessLoading(true);
    setCreateRoleAccessError("");
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_V1_BASE_URL}/roles`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to load roles");
      }

      const rolesList: any[] = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
          ? result.data.data
          : [];

      const matchedRole = rolesList.find(
        (r: any) =>
          r.role === role ||
          r.name === role ||
          r.roleName === role ||
          r.slug === role,
      );

      // Capture the actual role ID so it can be sent in the create-user payload
      const matchedRoleId = matchedRole?.id ?? matchedRole?._id ?? "";
      setFormData((prev) => ({ ...prev, roleId: matchedRoleId }));

      const rawPermissions = matchedRole?.permissions;
      const rawPages = matchedRole?.pages;

      const permissions: string[] = Array.isArray(rawPermissions)
        ? rawPermissions
        : [];
      const pageIds: string[] = Array.isArray(rawPages) ? rawPages : [];
      const pages = pagePermissions.filter((p) => pageIds.includes(p.id));

      setCreateRoleAccess({ permissions, pages });
      setCreateSelectedPermissions(permissions);
      setCreateSelectedPages(pages.map((p) => p.id));
    } catch (error) {
      setCreateRoleAccess(null);
      setCreateSelectedPermissions([]);
      setCreateSelectedPages([]);
      setFormData((prev) => ({ ...prev, roleId: "" }));
      setCreateRoleAccessError(
        error instanceof Error
          ? error.message
          : "Failed to load role permissions",
      );
    } finally {
      setCreateRoleAccessLoading(false);
    }
  };

  const applyRoleAccess = (roleId: string) => {
    setCreateRoleAccessError("");

    if (!roleId) {
      setCreateRoleAccess(null);
      setCreateSelectedPermissions([]);
      setCreateSelectedPages([]);
      return;
    }

    const matchedRole = rolesList.find((r: any) => r.id === roleId);

    if (!matchedRole) {
      setCreateRoleAccess(null);
      setCreateSelectedPermissions([]);
      setCreateSelectedPages([]);
      setCreateRoleAccessError(
        "Could not find access details for the selected role",
      );
      return;
    }

    // permissions come back as [{ permissionCode, module }]
    const rawPermissions: any[] = Array.isArray(matchedRole.permissions)
      ? matchedRole.permissions
      : [];
    const permissionCodes: string[] = rawPermissions
      .map((p) => p.permissionCode)
      .filter(Boolean);

    // The API has no "pages" concept yet — pages remain a frontend-only
    // mapping. If/when the backend adds pages per role, filter them here
    // the same way permissions are filtered above.
    setCreateRoleAccess({ permissions: permissionCodes, pages: [] });
    setCreateSelectedPermissions(permissionCodes);
    setCreateSelectedPages([]);
  };

  const validateForm = (isEdit: boolean = false): boolean => {
    const errors = {
      email: "",
      firstName: "",
      lastName: "",
    };
    let isValid = true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
      isValid = false;
    } else if (!isEdit && users.some((u) => u.email === formData.email)) {
      errors.email = "A user with this email already exists";
      isValid = false;
    }

    if (!formData.firstName) {
      errors.firstName = "First name is required";
      isValid = false;
    } else if (formData.firstName.length < 2) {
      errors.firstName = "First name must be at least 2 characters";
      isValid = false;
    }

    if (!formData.lastName) {
      errors.lastName = "Last name is required";
      isValid = false;
    } else if (formData.lastName.length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const checkUserLimit = (): boolean => {
    const activeUsers = users.filter(
      (u) => u.status?.toLowerCase() === "active",
    ).length;

    if (maxAllowedUsers > 0 && activeUsers >= maxAllowedUsers) {
      setShowUserLimitWarning(true);
      return false;
    }

    return true;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submits
    if (isCreatingUser) return;

    if (!hasPermission("USER_CREATE")) {
      toast.error("You don't have permission to create users.");
      return;
    }

    if (!formData.roleId) {
      toast.error("Please select a role before creating the user.");
      return;
    }

    // Check user limit
    if (!checkUserLimit()) return;

    setIsCreatingUser(true);
    const toastId = toast.loading("Creating user...");

    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_V1_BASE_URL}/users/create-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          roleId: formData.roleId,
          department: formData.department,
          phoneNumber: formData.phoneNumber,
          // permissions: createSelectedPermissions,
          // pages: createSelectedPages
        }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to create user");
      }

      const createdUser = result.data || {};

      const newUser: InternalUser = {
        id: createdUser.id || createdUser._id || Date.now().toString(),
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        roleId: formData.roleId,
        status: "active",
        createdAt: createdUser.createdAt || new Date().toISOString(),
        updatedAt: createdUser.updatedAt || new Date().toISOString(),
        createdBy: currentUser?.email || "unknown",
        department: formData.department,
        phoneNumber: formData.phoneNumber,
        // permissions: createSelectedPermissions,
        pages: createSelectedPages,
      };
      setUsers([newUser, ...users]);
      addAuditLog(
        "CREATE",
        newUser.id,
        newUser.email,
        `Created new user with role: ${newUser.role}`,
      );
      setShowCreateModal(false);
      resetForm();
      setCreateRoleAccess(null);
      setCreateSelectedPermissions([]);
      setCreateSelectedPages([]);

      toast.update(toastId, {
        render: `User ${newUser.firstName} ${newUser.lastName} created successfully!`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    } catch (error) {
      toast.update(toastId, {
        render:
          error instanceof Error ? error.message : "Failed to create user",
        type: "error",
        isLoading: false,
        autoClose: 4000,
        closeOnClick: true,
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasPermission("USER_UPDATE")) {
      toast.error("You don't have permission to update users.");
      return;
    }

    if (!selectedUser) return;

    try {
      await superAdminApi.updateUser(
        selectedUser.id,

        {
          firstName: formData.firstName,

          lastName: formData.lastName,

          role: formData.role.replace(/-/g, "_").toUpperCase(),

          department: formData.department,

          phoneNumber: formData.phoneNumber,
        },
      );

      setShowEditModal(false);

      await loadUsers();
      toast.success("User updated successfully.");
    } catch {
      toast.error("Failed to update user.");
    }
  };

  const handleDeactivateUser = async (user: InternalUser) => {
    if (!hasPermission("USER_DELETE")) {
      toast.error("You don't have permission to deactivate users.");
      return;
    }
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_V1_BASE_URL}/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          roleId: formData.roleId,
          department: formData.department,
          phoneNumber: formData.phoneNumber,
        }),
      });
      const result = await response.json().catch(() => null);

      await superAdminApi.deactivateUser(user.id);

      await loadUsers();

      toast.success("User deactivated successfully.");
    } catch (err) {
      toast.error("Unable to deactivate user.");
    }
  };

  const handleReactivateUser = async (user: InternalUser) => {
    if (!hasPermission("USER_UPDATE")) {
      toast.error("You don't have permission to reactivate users.");
      return;
    }
    try {
      await superAdminApi.reactivateUser(user.id);

      await loadUsers();

      toast.success("User reactivated successfully.");
    } catch (err) {
      toast.error("Unable to reactivate user.");
    }
  };

  const addAuditLog = (
    action: AuditLog["action"],
    userId: string,
    userEmail: string,
    details: string,
    location?: any,
  ) => {
    const newLog: AuditLog = {
      id: Date.now().toString(),
      action,
      userId,
      userEmail,
      performedBy: currentUser?.email || "unknown",
      performedByRole: currentUser?.role || "unknown",
      timestamp: new Date().toISOString(),
      details,
      ipAddress: "127.0.0.1",
      location: location ? `${location.city}, ${location.country}` : "Unknown",
      userAgent: navigator.userAgent,
    };
    setAuditLogs((prevLogs) => [newLog, ...prevLogs]);
  };

  const resetForm = () => {
    setFormData({
      email: "",
      firstName: "",
      lastName: "",
      role: "customer_viewer",
      roleId: "",
      department: "",
      phoneNumber: "",
    });
    setFormErrors({
      email: "",
      firstName: "",
      lastName: "",
    });
  };

  const openEditModal = (user: InternalUser) => {
    setSelectedUser(user);
    // Prefer an ID already present on the user record; fall back to
    // matching against the loaded roles list by name.
    const existingRoleId =
      (user as any).roleId ||
      rolesList.find(
        (r: any) => r.role_name === user.role || r.id === (user as any).roleId,
      )?.id ||
      "";
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      roleId: existingRoleId,
      department: user.department || "",
      phoneNumber: user.phoneNumber || "",
    });
    setShowEditModal(true);
  };

  const openUserAuditModal = (user: InternalUser) => {
    setSelectedUserForAudit(user);
    const userLogs = auditLogs.filter(
      (log) => log.userId === user.id || log.userEmail === user.email,
    );
    setUserAuditLogs(userLogs);
    setShowUserAuditModal(true);
  };

  const exportUserAuditLogs = () => {
    if (!selectedUserForAudit) return;

    // Prepare data for export
    const exportData = userAuditLogs.map((log) => ({
      Action: log.action,
      "User Email": log.userEmail,
      "Performed By": log.performedBy,
      "Performer Role": log.performedByRole,
      Timestamp: new Date(log.timestamp).toLocaleString(),
      Date: new Date(log.timestamp).toLocaleDateString(),
      Time: new Date(log.timestamp).toLocaleTimeString(),
      Location: log.location || "Unknown",
      "IP Address": log.ipAddress,
      "User Agent": log.userAgent || "N/A",
      Details: log.details,
      Metadata: JSON.stringify(log.metadata || {}),
    }));

    // Convert to CSV
    if (exportData.length === 0) {
      toast.warning("No audit logs available for this user.");
      return;
    }

    const headers = Object.keys(exportData[0]);
    const csvRows = [
      headers.join(","),
      ...exportData.map((row) =>
        headers
          .map((header) => {
            const value = row[header as keyof typeof row];
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${selectedUserForAudit.email}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(
      `Audit logs exported for ${selectedUserForAudit.firstName} ${selectedUserForAudit.lastName}.`,
    );
  };

  const exportAuditLogs = () => {
    // Prepare data for export
    const exportData = auditLogs.map((log) => ({
      Action: log.action,
      "User Email": log.userEmail,
      "Performed By": log.performedBy,
      "Performer Role": log.performedByRole,
      Timestamp: new Date(log.timestamp).toLocaleString(),
      Date: new Date(log.timestamp).toLocaleDateString(),
      Time: new Date(log.timestamp).toLocaleTimeString(),
      Location: log.location || "Unknown",
      "IP Address": log.ipAddress,
      "User Agent": log.userAgent || "N/A",
      Details: log.details,
      Metadata: JSON.stringify(log.metadata || {}),
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvRows = [
      headers.join(","),
      ...exportData.map((row) =>
        headers
          .map((header) => {
            const value = row[header as keyof typeof row];
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Audit logs exported successfully.");
  };

  const exportUploadHistory = () => {
    const exportData = uploadHistory.map((upload) => ({
      "File Name": upload.fileName,
      "File Size": upload.fileSize,
      "Upload Type": upload.uploadType,
      "Uploaded By": upload.uploadedBy,
      Timestamp: new Date(upload.timestamp).toLocaleString(),
      Status: upload.status,
      "Rows Processed": upload.metadata.rowsProcessed || "N/A",
      "Validation Errors": upload.metadata.validationErrors || 0,
      "Error Message": upload.metadata.errorMessage || "N/A",
    }));

    const headers = Object.keys(exportData[0]);
    const csvRows = [
      headers.join(","),
      ...exportData.map((row) =>
        headers
          .map((header) => {
            const value = row[header as keyof typeof row];
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      ),
    ];

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `upload_history_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Upload history exported successfully.");
  };

  const viewRolePermissions = (role: InternalUser["role"]) => {
    const roleDef = roleDefinitions.find((r) => r.role === role);
    if (roleDef) {
      setSelectedRolePermissions({ ...roleDef });
      setEditablePermissions([...roleDef.permissions]);
      setEditablePages([...roleDef.pages]);
      setIsEditingPermissions(false);
      setNewPermission("");
      setNewPageId("");
      setShowPermissionsModal(true);
    }
  };

  const handleAddPermission = () => {
    if (newPermission && !editablePermissions.includes(newPermission)) {
      setEditablePermissions([...editablePermissions, newPermission]);
      setNewPermission("");
    }
  };

  const handleRemovePermission = (permission: string) => {
    setEditablePermissions(editablePermissions.filter((p) => p !== permission));
  };

  const handleAddPage = () => {
    if (newPageId && !editablePages.includes(newPageId)) {
      setEditablePages([...editablePages, newPageId]);
      setNewPageId("");
    }
  };

  const handleRemovePage = (pageId: string) => {
    setEditablePages(editablePages.filter((p) => p !== pageId));
  };

  const handleSavePermissions = () => {
    if (!hasPermission("ROLE_UPDATE")) {
      toast.error("You don't have permission to update role permissions.");
      return;
    }
    if (selectedRolePermissions) {
      // Update the role definitions
      const updatedRoleDefs = roleDefinitions.map((role) =>
        role.role === selectedRolePermissions.role
          ? {
              ...role,
              permissions: editablePermissions,
              pages: editablePages,
            }
          : role,
      );
      setRoleDefinitions(updatedRoleDefs);

      // Add audit log for permission changes
      addAuditLog(
        "UPDATE",
        "system",
        "system",
        `Updated permissions for role: ${selectedRolePermissions.name}`,
      );
      setIsEditingPermissions(false);
      toast.success(`Permissions updated for ${selectedRolePermissions.name}.`);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const matched = rolesList.find(
      (r: any) => r.role === role || r.role_name?.toLowerCase() === role,
    );
    if (matched) return matched.description || matched.role_name;

    return role
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const getRoleBadgeColor = (role: string) => {
    if (role.includes("admin")) return "bg-purple-100 text-purple-800";
    if (role.includes("manager")) return "bg-blue-100 text-blue-800";
    if (role.includes("operator")) return "bg-teal-100 text-teal-800";
    if (role.includes("viewer")) return "bg-gray-100 text-gray-800";
    return "bg-gray-100 text-gray-800";
  };

  const getRoleIcon = (role: string) => {
    if (role.includes("admin")) return "👑";
    if (role.includes("manager")) return "📊";
    if (role.includes("operator")) return "🛠️";
    if (role.includes("viewer")) return "👁️";
    return "👤";
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "LOGIN":
        return "🔐";
      case "LOGOUT":
        return "🚪";
      case "CREATE":
        return "➕";
      case "UPDATE":
        return "✏️";
      case "DEACTIVATE":
        return "⛔";
      case "REACTIVATE":
        return "✅";
      case "ROLE_CHANGE":
        return "🔄";
      case "UPLOAD":
        return "📤";
      default:
        return "📝";
    }
  };

  const canViewAllAuditLogs =
    currentUser?.role?.toLowerCase?.().replace(/[\s-]/g, "_") === "super_admin";

  return (
    <>
      <div className="space-y-6">
        {/* Upgrade / Raise Ticket Modal */}
        {showUserLimitWarning && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {ticketStatus === "raised" ? (
                <div className="p-8 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl mb-4">
                    ✅
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Ticket Raised
                  </h3>
                  <p className="text-gray-600 mt-2 text-sm">
                    Your upgrade request has been submitted. Our team will
                    contact you shortly.
                  </p>
                  <div className="mt-4 inline-block px-4 py-2 rounded-lg bg-gray-100 font-mono text-sm text-gray-800">
                    {ticketId}
                  </div>
                  {ticketSummary && (
                    <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-left space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Requested users</span>
                        <span className="font-semibold text-gray-900">
                          {ticketSummary.users}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Requested storage</span>
                        <span className="font-semibold text-gray-900">
                          {ticketSummary.storage}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="mt-6">
                    <button
                      onClick={closeLimitModal}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="px-6 py-5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center text-2xl">
                        ⚠️
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          User Limit Reached
                        </h3>
                        <p className="text-xs text-amber-700">
                          Your current plan can't add more users
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 text-sm">
                      Your plan allows a maximum of{" "}
                      <strong>{maxAllowedUsers}</strong> users. Tell us what you
                      need and we'll raise an upgrade ticket.
                    </p>

                    <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Active users</span>
                        <span className="font-semibold text-gray-900">
                          {users.filter((u) => u.status === "active").length} /{" "}
                          {maxAllowedUsers}
                        </span>
                      </div>
                      {tenantPlan && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Current storage</span>
                          <span className="font-semibold text-gray-900">
                            {tenantPlan.storageValue} {tenantPlan.storageUnit}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Maximum users required{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min={maxAllowedUsers + 1}
                          value={ticketForm.requestedUsers}
                          onChange={(e) => {
                            setTicketForm({
                              ...ticketForm,
                              requestedUsers: e.target.value,
                            });
                            setTicketError("");
                          }}
                          disabled={ticketStatus === "submitting"}
                          placeholder={`e.g. ${maxAllowedUsers + 5}`}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Storage required{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={ticketForm.requestedStorageValue}
                            onChange={(e) => {
                              setTicketForm({
                                ...ticketForm,
                                requestedStorageValue: e.target.value,
                              });
                              setTicketError("");
                            }}
                            disabled={ticketStatus === "submitting"}
                            placeholder="e.g. 100"
                            className="flex-1 min-w-0 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <select
                            value={ticketForm.requestedStorageUnit}
                            onChange={(e) =>
                              setTicketForm({
                                ...ticketForm,
                                requestedStorageUnit: e.target.value as
                                  | "MB"
                                  | "GB"
                                  | "TB",
                              })
                            }
                            disabled={ticketStatus === "submitting"}
                            className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="MB">MB</option>
                            <option value="GB">GB</option>
                            <option value="TB">TB</option>
                          </select>
                        </div>
                      </div>
                      {ticketError && (
                        <p className="text-xs text-red-600">{ticketError}</p>
                      )}
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        disabled={isCreatingUser}
                        onClick={() => {
                          setShowCreateModal(false);
                          resetForm();
                          setCreateRoleAccess(null);
                          setCreateSelectedPermissions([]);
                          setCreateSelectedPages([]);
                        }}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRaiseTicket}
                        disabled={ticketStatus === "submitting"}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 flex items-center gap-2 disabled:opacity-70"
                      >
                        {ticketStatus === "submitting" && (
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        )}
                        {ticketStatus === "submitting"
                          ? "Raising..."
                          : "Raise Ticket to Upgrade"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal &&
          tenantPlan &&
          (() => {
            const total =
              tenantPlan.maxUsers * DEMO_PRICING.perUser +
              toGb(tenantPlan.storageValue, tenantPlan.storageUnit) *
                DEMO_PRICING.perGb;
            return (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                  {paymentStatus === "processing" ? (
                    <div className="p-12 text-center">
                      <div className="mx-auto w-14 h-14 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <h3 className="text-lg font-semibold text-gray-900 mt-6">
                        Processing payment...
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Please don't close this window.
                      </p>
                    </div>
                  ) : paymentStatus === "success" ? (
                    <div className="p-12 text-center">
                      <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-3xl">
                        ✅
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mt-5">
                        Payment Successful
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Your plan is now active.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="px-6 py-5 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-gray-100">
                        <h3 className="text-xl font-bold text-gray-900">
                          Activate Your Plan
                        </h3>
                        <p className="text-sm text-blue-700/70 mt-0.5">
                          Complete payment to start creating users.
                        </p>
                      </div>
                      <div className="p-6 space-y-3">
                        <div className="flex justify-between items-center rounded-xl border border-gray-200 p-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">👥</span>
                            <span className="text-sm text-gray-600">Users</span>
                          </div>
                          <span className="font-semibold text-gray-900">
                            {tenantPlan.maxUsers}
                          </span>
                        </div>
                        <div className="flex justify-between items-center rounded-xl border border-gray-200 p-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">💾</span>
                            <span className="text-sm text-gray-600">
                              Storage
                            </span>
                          </div>
                          <span className="font-semibold text-gray-900">
                            {tenantPlan.storageValue} {tenantPlan.storageUnit}
                          </span>
                        </div>
                        <div className="flex justify-between items-center rounded-xl bg-blue-50 border border-blue-200 p-4">
                          <span className="text-sm font-medium text-blue-900">
                            Total payable
                          </span>
                          <span className="text-xl font-bold text-blue-700">
                            {formatUSD(total)}
                          </span>
                        </div>
                        <div className="flex justify-end gap-3 pt-3">
                          <button
                            onClick={() => setShowPaymentModal(false)}
                            className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handlePayNow}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700"
                          >
                            Pay Now
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

        {/* User Audit Log Modal */}
        {showUserAuditModal && selectedUserForAudit && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      Audit Logs: {selectedUserForAudit.firstName}{" "}
                      {selectedUserForAudit.lastName}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Email: {selectedUserForAudit.email} | Role:{" "}
                      {selectedUserForAudit.role}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={exportUserAuditLogs}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      📥 Export User Logs
                    </button>
                    <button
                      onClick={() => setShowUserAuditModal(false)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">
                      Total Activities: <strong>{userAuditLogs.length}</strong>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-green-100 rounded">
                        Login/Logout
                      </span>
                      <span className="px-2 py-1 bg-blue-100 rounded">
                        User Management
                      </span>
                      <span className="px-2 py-1 bg-purple-100 rounded">
                        Role Changes
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {userAuditLogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-6xl mb-3">📋</div>
                      <p>No audit logs found for this user</p>
                    </div>
                  ) : (
                    userAuditLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:shadow-sm transition"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">
                                {getActionIcon(log.action)}
                              </span>
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  log.action === "LOGIN"
                                    ? "bg-green-100 text-green-700"
                                    : log.action === "LOGOUT"
                                      ? "bg-gray-100 text-gray-700"
                                      : log.action === "CREATE"
                                        ? "bg-green-100 text-green-700"
                                        : log.action === "UPDATE"
                                          ? "bg-blue-100 text-blue-700"
                                          : log.action === "DEACTIVATE"
                                            ? "bg-orange-100 text-orange-700"
                                            : log.action === "REACTIVATE"
                                              ? "bg-green-100 text-green-700"
                                              : log.action === "DELETE"
                                                ? "bg-red-100 text-red-700"
                                                : "bg-purple-100 text-purple-700"
                                }`}
                              >
                                {log.action}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {log.userEmail}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">
                              {log.details}
                            </p>
                            <div className="text-xs text-gray-500 mt-2 space-y-1">
                              <p>
                                Performed by: {log.performedBy} (
                                {log.performedByRole})
                              </p>
                              {log.location && (
                                <p>📍 Location: {log.location}</p>
                              )}
                              <p>🌐 IP Address: {log.ipAddress}</p>
                              {log.userAgent && (
                                <p>💻 Device: {log.userAgent}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(log.timestamp).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowUserAuditModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header Info Banner */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">👥</div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 text-lg">
                Internal User Management
              </h3>
              <p className="text-sm text-green-700 mt-1">
                Create and manage internal users within your organization.
                Assign roles to control access to different pages and features.
                All actions are logged for audit compliance.
              </p>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <span>✓</span> Role-Based Access Control
                </div>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <span>✓</span> Page-Level Permissions
                </div>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <span>✓</span> Full Audit Trail
                </div>
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <span>✓</span> Self-Service Management
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl mb-2">👥</div>
            <div className="text-2xl font-bold text-gray-800">
              {users.length}
            </div>
            <div className="text-sm text-gray-500">Total Users</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl mb-2">✅</div>
            <div className="text-2xl font-bold text-green-600">
              {users.filter((u) => u.status === "active").length}
            </div>
            <div className="text-sm text-gray-500">Active Users</div>
            <div className="text-xs text-gray-400 mt-1">
              <p className="text-sm text-gray-500">Max: {maxAllowedUsers}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl mb-2">⛔</div>
            <div className="text-2xl font-bold text-red-600">
              {users.filter((u) => u.status === "deactivated").length}
            </div>
            <div className="text-sm text-gray-500">Deactivated</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl mb-2">👑</div>
            <div className="text-2xl font-bold text-purple-600">
              {users.filter((u) => u.role.includes("admin")).length}
            </div>
            <div className="text-sm text-gray-500">Admins</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl mb-2">📊</div>
            <div className="text-2xl font-bold text-blue-600">
              {users.filter((u) => u.role.includes("manager")).length}
            </div>
            <div className="text-sm text-gray-500">Managers</div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div className="flex gap-3 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active Users</option>
              <option value="deactivated">Deactivated Users</option>
            </select>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              {rolesList.map((r: any) => (
                <option
                  key={r.id}
                  value={(r.role || r.role_name || "").toLowerCase()}
                >
                  {r.description || r.role_name}
                </option>
              ))}
            </select>

            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, department..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>

            <button
              onClick={() => setShowAuditLogs(!showAuditLogs)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
            >
              📋 {showAuditLogs ? "Hide" : "Show"} Audit Logs
            </button>
          </div>

          {canCreateUser && (
            <button
              onClick={handleCreateUserClick}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 shadow-sm"
            >
              <span className="text-xl">+</span> Create User
            </button>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              Internal Users
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage users within your organization
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    User
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Role & Access
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Department
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Last Login
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Created By
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                          {user.phoneNumber && (
                            <div className="text-xs text-gray-400">
                              {user.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {getRoleIcon(user.role)}
                          </span>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(user.role)}`}
                          >
                            {getRoleDisplayName(user.role)}
                          </span>
                        </div>
                        {/* <button
                                                onClick={() => viewRolePermissions(user.role)}
                                                className="text-xs text-blue-600 hover:text-blue-800 block"
                                            >
                                                View & Edit Permissions →
                                            </button> */}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {user.department || "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.status === "active" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-green-100 text-green-700">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-red-100 text-red-700">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                          Deactivated
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : "Never"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-700">
                        {user.createdBy}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 flex-nowrap">
                        {canUpdateUser && (
                          <IconButton
                            icon={<Pencil size={18} />}
                            label="Edit"
                            colorClass="text-blue-600 hover:bg-blue-50"
                            onClick={() => openEditModal(user)}
                          />
                        )}

                        {canAuditUser && (
                          <IconButton
                            icon={<ClipboardList size={18} />}
                            label="Audit Logs"
                            colorClass="text-purple-600 hover:bg-purple-50"
                            onClick={() => openUserAuditModal(user)}
                          />
                        )}

                        {user.status === "active"
                          ? canDeleteUser && (
                              <IconButton
                                icon={<Power size={18} />}
                                label="Deactivate"
                                colorClass="text-orange-600 hover:bg-orange-50"
                                onClick={() => handleDeactivateUser(user)}
                              />
                            )
                          : canUpdateUser && (
                              <IconButton
                                icon={<RotateCcw size={18} />}
                                label="Reactivate"
                                colorClass="text-green-600 hover:bg-green-50"
                                onClick={() => handleReactivateUser(user)}
                              />
                            )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <div className="text-6xl mb-3">👥</div>
                      <p>No users found</p>
                      <p className="text-sm mt-1">
                        Try changing your filters or create a new user
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit Logs Section */}
        {false && showAuditLogs && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Audit Logs
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {canViewAllAuditLogs
                    ? "Showing all audit activity from the database"
                    : "Showing login and logout activity from the database"}
                </p>
              </div>
              <button
                onClick={exportAuditLogs}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                📥 Export to CSV
              </button>
            </div>
            <div className="p-6">
              <div className="mb-5 max-w-sm">
                <label
                  htmlFor="customer-audit-tenant-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Organization
                </label>
                <select
                  id="customer-audit-tenant-filter"
                  value={selectedAuditTenantId}
                  onChange={(event) => {
                    const auditTenantId = event.target.value;
                    setSelectedAuditTenantId(auditTenantId);
                    loadAuditLogs(auditTenantId);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">
                    {canViewAllAuditLogs
                      ? "All organizations"
                      : "My organization"}
                  </option>
                  {auditTenants.map((auditTenant) => (
                    <option key={auditTenant.id} value={auditTenant.id}>
                      {auditTenant.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {auditLogsLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading audit logs...
                  </div>
                ) : auditLogsError ? (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {auditLogsError}
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No audit logs found.
                  </div>
                ) : (
                  auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:shadow-sm transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              {getActionIcon(log.action)}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                log.action === "LOGIN"
                                  ? "bg-green-100 text-green-700"
                                  : log.action === "LOGOUT"
                                    ? "bg-gray-100 text-gray-700"
                                    : log.action === "CREATE"
                                      ? "bg-green-100 text-green-700"
                                      : log.action === "UPDATE"
                                        ? "bg-blue-100 text-blue-700"
                                        : log.action === "DEACTIVATE"
                                          ? "bg-orange-100 text-orange-700"
                                          : log.action === "REACTIVATE"
                                            ? "bg-green-100 text-green-700"
                                            : log.action === "DELETE"
                                              ? "bg-red-100 text-red-700"
                                              : "bg-purple-100 text-purple-700"
                              }`}
                            >
                              {log.action}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {log.userEmail}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{log.details}</p>
                          <div className="text-xs text-gray-500 mt-2 space-y-1">
                            <p>
                              Performed by: {log.performedBy} (
                              {log.performedByRole})
                            </p>
                            {log.location && <p>📍 Location: {log.location}</p>}
                            <p>🌐 IP Address: {log.ipAddress}</p>
                            {log.userAgent && <p>💻 Device: {log.userAgent}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs Section with organization and action filters */}
        {showAuditLogs && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Audit Logs
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Showing audit activity from the database
                </p>
              </div>
              <button
                onClick={exportAuditLogs}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                📥 Export to CSV
              </button>
            </div>
            <div className="p-6">
              <div className="mb-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <label
                    htmlFor="customer-audit-tenant-filter"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Organization
                  </label>
                  <select
                    id="customer-audit-tenant-filter"
                    value={selectedAuditTenantId}
                    onChange={(event) => {
                      const auditTenantId = event.target.value;
                      setSelectedAuditTenantId(auditTenantId);
                      setSelectedAuditAction("");
                      loadAuditActions(auditTenantId);
                      loadAuditLogs(auditTenantId, "");
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      {canViewAllAuditLogs
                        ? "All organizations"
                        : "My organization"}
                    </option>
                    {auditTenants.map((auditTenant) => (
                      <option key={auditTenant.id} value={auditTenant.id}>
                        {auditTenant.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="customer-audit-action-filter"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Action
                  </label>
                  <select
                    id="customer-audit-action-filter"
                    value={selectedAuditAction}
                    onChange={(event) => {
                      const action = event.target.value;
                      setSelectedAuditAction(action);
                      loadAuditLogs(selectedAuditTenantId, action);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All actions</option>
                    {availableAuditActions.map((action) => (
                      <option key={action} value={action}>
                        {formatAuditAction(action)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {auditLogsLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading audit logs...
                  </div>
                ) : auditLogsError ? (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {auditLogsError}
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No audit logs found.
                  </div>
                ) : (
                  auditLogs.map((log) => (
                    <div
                      key={log.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:shadow-sm transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              {getActionIcon(log.action)}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                log.action === "LOGIN"
                                  ? "bg-green-100 text-green-700"
                                  : log.action === "LOGOUT"
                                    ? "bg-gray-100 text-gray-700"
                                    : log.action === "CREATE"
                                      ? "bg-green-100 text-green-700"
                                      : log.action === "UPDATE"
                                        ? "bg-blue-100 text-blue-700"
                                        : log.action === "DEACTIVATE"
                                          ? "bg-orange-100 text-orange-700"
                                          : log.action === "REACTIVATE"
                                            ? "bg-green-100 text-green-700"
                                            : log.action === "DELETE"
                                              ? "bg-red-100 text-red-700"
                                              : "bg-purple-100 text-purple-700"
                              }`}
                            >
                              {log.action}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {log.userEmail}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{log.details}</p>
                          <div className="text-xs text-gray-500 mt-2 space-y-1">
                            <p>
                              Performed by: {log.performedBy} (
                              {log.performedByRole})
                            </p>
                            {log.location && (
                              <p>:round_pushpin: Location: {log.location}</p>
                            )}
                            <p>
                              :globe_with_meridians: IP Address: {log.ipAddress}
                            </p>
                            {log.userAgent && (
                              <p>:computer: Device: {log.userAgent}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Permissions Modal with Edit Capability */}
        {showPermissionsModal && selectedRolePermissions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {selectedRolePermissions.name} - Role Permissions
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedRolePermissions.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!isEditingPermissions ? (
                      <button
                        onClick={() => setIsEditingPermissions(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        ✏️ Edit Permissions
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setIsEditingPermissions(false);
                            setEditablePermissions([
                              ...selectedRolePermissions.permissions,
                            ]);
                            setEditablePages([
                              ...selectedRolePermissions.pages,
                            ]);
                          }}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSavePermissions}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          💾 Save Changes
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setShowPermissionsModal(false)}
                      className="text-gray-400 hover:text-gray-600 text-2xl ml-2"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Permissions Section */}
                <div className="mb-8">
                  <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span>🔑</span> System Permissions
                  </h4>
                  {isEditingPermissions ? (
                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {editablePermissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                          >
                            {perm}
                            <button
                              onClick={() => handleRemovePermission(perm)}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <select
                          value={newPermission}
                          onChange={(e) => setNewPermission(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select permission to add...</option>
                          {availablePermissions
                            .filter((p) => !editablePermissions.includes(p))
                            .map((perm) => (
                              <option key={perm} value={perm}>
                                {perm}
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={handleAddPermission}
                          disabled={!newPermission}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          + Add Permission
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedRolePermissions.permissions.map((perm) => (
                        <span
                          key={perm}
                          className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                        >
                          {perm}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Accessible Pages Section */}
                <div className="mb-8">
                  <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span>📄</span> Accessible Pages & Features
                  </h4>
                  {isEditingPermissions ? (
                    <div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {editablePages.map((pageId) => {
                          const page = pagePermissions.find(
                            (p) => p.id === pageId,
                          );
                          return page ? (
                            <div
                              key={page.id}
                              className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 relative group"
                            >
                              <span className="text-2xl">{page.icon}</span>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {page.pageName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {page.description}
                                </p>
                                <code className="text-xs text-gray-400">
                                  {page.path}
                                </code>
                              </div>
                              <button
                                onClick={() => handleRemovePage(page.id)}
                                className="absolute top-2 right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition"
                              >
                                ×
                              </button>
                            </div>
                          ) : null;
                        })}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <select
                          value={newPageId}
                          onChange={(e) => setNewPageId(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 flex-1"
                        >
                          <option value="">Select page to add...</option>
                          {pagePermissions
                            .filter((p) => !editablePages.includes(p.id))
                            .map((page) => (
                              <option key={page.id} value={page.id}>
                                {page.icon} {page.pageName}
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={handleAddPage}
                          disabled={!newPageId}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          + Add Page
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {pagePermissions
                        .filter((p) =>
                          selectedRolePermissions.pages.includes(p.id),
                        )
                        .map((page) => (
                          <div
                            key={page.id}
                            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <span className="text-2xl">{page.icon}</span>
                            <div>
                              <p className="font-medium text-gray-900">
                                {page.pageName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {page.description}
                              </p>
                              <code className="text-xs text-gray-400">
                                {page.path}
                              </code>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Summary Section */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Permission Summary
                  </p>
                  <div className="space-y-1 text-xs text-blue-800">
                    <p>✓ Total Permissions: {editablePermissions.length}</p>
                    <p>✓ Total Accessible Pages: {editablePages.length}</p>
                    {isEditingPermissions && (
                      <p className="text-orange-600 mt-2">
                        ⚠️ Changes will be applied to all users with this role
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Create User
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                      setCreateRoleAccess(null);
                      setCreateSelectedPermissions([]);
                      setCreateSelectedPages([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Create a new user and assign role-based permissions
                </p>
              </div>

              <form onSubmit={handleCreateUser} autoComplete="off">
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        className={`w-full px-3 py-2 border rounded-lg ${formErrors.firstName ? "border-red-500" : "border-gray-300"}`}
                      />
                      {formErrors.firstName && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg ${formErrors.lastName ? "border-red-500" : "border-gray-300"}`}
                      />
                      {formErrors.lastName && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.lastName}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className={`w-full px-3 py-2 border rounded-lg ${formErrors.email ? "border-red-500" : "border-gray-300"}`}
                        placeholder="user@company.com"
                        autoComplete="off"
                        name="new-user-email"
                      />
                      {formErrors.email && (
                        <p className="text-xs text-red-500 mt-1">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.roleId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedRole = rolesList.find(
                            (r: any) => r.id === selectedId,
                          );
                          setFormData({
                            ...formData,
                            roleId: selectedId,
                            role: selectedRole?.role_name || "",
                          });
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: "right 12px center",
                          backgroundSize: "20px 20px",
                          backgroundRepeat: "no-repeat",
                          paddingRight: "40px",
                        }}
                        disabled={rolesListLoading}
                      >
                        <option value="" disabled>
                          {rolesListLoading
                            ? "Loading roles..."
                            : "Select a role"}
                        </option>
                        {rolesList.map((r: any) => (
                          <option key={r.id} value={r.id} className="py-1">
                            {r.description || r.role_name}
                          </option>
                        ))}
                      </select>
                      {rolesListError && (
                        <p className="text-xs text-red-500 mt-1">
                          {rolesListError}
                        </p>
                      )}
                      <div className="mt-2 min-h-[20px]">
                        <p className="text-xs text-gray-500">
                          {
                            rolesList.find((r: any) => r.id === formData.roleId)
                              ?.description
                          }
                        </p>
                      </div>

                      <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                        Access for this role
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Uncheck any permission or page you don't want to grant
                        this user.
                      </p>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto min-h-[30px]">
                        {createRoleAccessLoading ? (
                          <div className="flex items-center justify-center h-full">
                            <p className="text-xs text-gray-500">
                              Loading access list...
                            </p>
                          </div>
                        ) : createRoleAccessError ? (
                          <div className="flex items-center justify-center h-full">
                            <p className="text-xs text-red-500">
                              {createRoleAccessError}
                            </p>
                          </div>
                        ) : createRoleAccess ? (
                          <>
                            {Array.isArray(createRoleAccess.permissions) &&
                              createRoleAccess.permissions.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-600 mb-1.5">
                                    Permissions
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {createRoleAccess.permissions.map(
                                      (perm) => (
                                        <label
                                          key={perm}
                                          className="flex items-center gap-2 text-xs text-gray-700 bg-white px-2.5 py-1.5 rounded-md border border-gray-100 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={createSelectedPermissions.includes(
                                              perm,
                                            )}
                                            onChange={() =>
                                              toggleCreatePermission(perm)
                                            }
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                          />
                                          <span className="font-medium">
                                            {perm}
                                          </span>
                                        </label>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                            {Array.isArray(createRoleAccess.pages) &&
                              createRoleAccess.pages.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 mb-1.5">
                                    Pages
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {createRoleAccess.pages.map((page) => (
                                      <label
                                        key={page.id}
                                        className="flex items-center gap-2 text-xs text-gray-700 bg-white px-2.5 py-1.5 rounded-md border border-gray-100 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={createSelectedPages.includes(
                                            page.id,
                                          )}
                                          onChange={() =>
                                            toggleCreatePage(page.id)
                                          }
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-blue-500">
                                          {page.icon}
                                        </span>
                                        <span className="font-medium">
                                          {page.pageName}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}

                            {Array.isArray(createRoleAccess.permissions) &&
                              createRoleAccess.permissions.length === 0 &&
                              Array.isArray(createRoleAccess.pages) &&
                              createRoleAccess.pages.length === 0 && (
                                <div className="flex items-center justify-center h-30">
                                  <p className="text-xs text-gray-500">
                                    No access data returned for this role
                                  </p>
                                </div>
                              )}
                          </>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <p className="text-xs text-gray-500">
                              Select a role to preview access
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., Marketing, Sales, IT"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="+1 234-567-8900"
                      />
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <span>🔒</span> Role-Based Access Summary
                    </p>
                    <div className="space-y-2 text-xs text-blue-800">
                      <p>
                        ✓ User will have{" "}
                        {formData.role.includes("admin")
                          ? "full administrative"
                          : formData.role.includes("manager")
                            ? "manager"
                            : "read-only"}{" "}
                        access
                      </p>
                      <p>
                        ✓ Accessible pages:{" "}
                        {createSelectedPages.length ||
                          roleDefinitions.find((r) => r.role === formData.role)
                            ?.pages.length}{" "}
                        pages
                      </p>
                      <p>
                        ✓ A temporary password will be generated and emailed to
                        the user, who must reset it on first login
                      </p>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                      setCreateRoleAccess(null);
                      setCreateSelectedPermissions([]);
                      setCreateSelectedPages([]);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingUser}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isCreatingUser ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <span>✓</span> Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Edit User
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Update user information and role
                </p>
              </div>

              <form onSubmit={handleUpdateUser}>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role
                      </label>
                      <select
                        value={formData.roleId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedRole = rolesList.find(
                            (r: any) => r.id === selectedId,
                          );
                          setFormData({
                            ...formData,
                            roleId: selectedId,
                            role: selectedRole?.role_name || "",
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        disabled={rolesListLoading}
                      >
                        <option value="" disabled>
                          {rolesListLoading
                            ? "Loading roles..."
                            : "Select a role"}
                        </option>
                        {rolesList.map((r: any) => (
                          <option key={r.id} value={r.id}>
                            {r.description || r.role_name}
                          </option>
                        ))}
                      </select>
                      {selectedUser.role !== formData.role && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠️ Changing role will update user's page permissions
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Department
                      </label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            department: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </>
  );
};

export default CustomerUserManagement;
